/**
 * Todo Recurrence Service
 * Handles recurring task creation and management
 * Part of the modularized todo service architecture
 */

import { IService, ServiceResponse, createSuccessResponse, createErrorResponse } from '../service-registry';
import { typedSupabase } from '@/lib/typed-supabase';
import { CreateTodoRequest, Todo } from '@/types/todo';
import { addDays, formatISO } from 'date-fns';
import { logger } from '@/lib/logger';

export interface RecurrencePattern {
  frequency: 'daily' | 'weekly' | 'monthly' | 'yearly';
  interval: number; // Every X days/weeks/months/years
  endDate?: string; // Optional end date for recurrence
  maxOccurrences?: number; // Optional max number of occurrences
}

export interface ITodoRecurrenceService extends IService {
  createRecurringTodo(baseTodo: CreateTodoRequest, pattern: RecurrencePattern): Promise<ServiceResponse<Todo[]>>;
  expandRecurringInstances(baseTodoId: string, count?: number): Promise<ServiceResponse<Todo[]>>;
  updateRecurrencePattern(todoId: string, pattern: RecurrencePattern): Promise<ServiceResponse<Todo>>;
  getNextOccurrence(baseTodoId: string): Promise<ServiceResponse<Todo | null>>;
  getRecurringInstances(baseTodoId: string): Promise<ServiceResponse<Todo[]>>;
}

export class TodoRecurrenceService implements ITodoRecurrenceService {
  readonly name = 'TodoRecurrenceService';
  private initialized = false;

  async initialize(): Promise<void> {
    this.initialized = true;
    logger.info('TodoRecurrenceService initialized');
  }

  async dispose(): Promise<void> {
    this.initialized = false;
    logger.info('TodoRecurrenceService disposed');
  }

  async createRecurringTodo(baseTodo: CreateTodoRequest, pattern: RecurrencePattern): Promise<ServiceResponse<Todo[]>> {
    try {
      logger.debug('Creating recurring todo', {
        title: baseTodo.title,
        frequency: pattern.frequency,
        interval: pattern.interval
      });

      const createdTodos: Todo[] = [];
      const maxInstances = pattern.maxOccurrences ?? 10; // Default to 10 instances

      // Create the base todo first
      const baseResult = await this.createSingleTodo(baseTodo, baseTodo.assigned_by);
      if (!baseResult.success) {
        return createErrorResponse(baseResult.error);
      }

      const baseTodoCreated = baseResult.data!;
      createdTodos.push(baseTodoCreated);

      // Create recurring instances
      let currentDate = new Date(baseTodo.due_date ?? baseTodoCreated.created_at);
      let occurrencesCreated = 1;

      while (occurrencesCreated < maxInstances) {
        // Calculate next occurrence based on pattern
        currentDate = this.calculateNextOccurrence(currentDate, pattern);

        // Check if we've exceeded the end date
        if (pattern.endDate && currentDate > new Date(pattern.endDate)) {
          break;
        }

        // Create the next instance
        const nextTodo = {
          ...baseTodo,
          title: `${baseTodo.title} (${occurrencesCreated + 1})`,
          due_date: formatISO(currentDate),
          recurrence_parent_id: baseTodoCreated.id,
        };

        const nextResult = await this.createSingleTodo(nextTodo);
        if (nextResult.success) {
          createdTodos.push(nextResult.data!);
          occurrencesCreated++;
        } else {
          logger.warn('Failed to create recurring instance', {
            instance: occurrencesCreated + 1,
            error: nextResult.error
          });
          break;
        }
      }

      logger.info('Recurring todo series created', {
        baseId: baseTodoCreated.id,
        totalInstances: createdTodos.length,
        pattern: pattern.frequency
      });

      return createSuccessResponse(createdTodos);

    } catch (error) {
      logger.error('Error in createRecurringTodo', {
        error: error instanceof Error ? error.message : error,
        title: baseTodo.title
      });
      return createErrorResponse(error instanceof Error ? error.message : 'Create recurring todo failed');
    }
  }

  async expandRecurringInstances(baseTodoId: string, count: number = 5): Promise<ServiceResponse<Todo[]>> {
    try {
      logger.debug('Expanding recurring instances', { baseTodoId, count });

      // Get the base todo
      const { data: baseTodo, error: baseError } = await typedSupabase
        .from('todos')
        .select('*')
        .eq('id', baseTodoId)
        .single();

      if (baseError || !baseTodo) {
        logger.error('Base todo not found for expansion', { baseTodoId, error: baseError?.message });
        return createErrorResponse('Base todo not found');
      }

      // Get existing instances
      const { data: existingInstances, error: instancesError } = await typedSupabase
        .from('todos')
        .select('*')
        .eq('recurring_parent_id', baseTodoId)
        .order('due_date', { ascending: true });

      if (instancesError) {
        logger.error('Failed to get existing instances', { error: instancesError.message, baseTodoId });
        return createErrorResponse('Failed to get existing instances');
      }

      const newInstances: Todo[] = [];
      const totalExisting = (existingInstances?.length || 0) + 1; // +1 for base todo

      // Calculate pattern from existing instances
      const pattern = this.inferRecurrencePattern(baseTodo as Todo, (existingInstances || []) as Todo[]);
      if (!pattern) {
        return createErrorResponse('Could not infer recurrence pattern');
      }

      // Find the latest instance
      const latestInstance = existingInstances?.[existingInstances.length - 1] || baseTodo;
      let currentDate = new Date(latestInstance.due_date ?? latestInstance.created_at);

      // Create additional instances
      for (let i = 0; i < count; i++) {
        currentDate = this.calculateNextOccurrence(currentDate, pattern);

        if (pattern.endDate && currentDate > new Date(pattern.endDate)) {
          break;
        }

        const priority =
          baseTodo.priority === 'low' ||
          baseTodo.priority === 'medium' ||
          baseTodo.priority === 'high'
            ? baseTodo.priority
            : undefined;

        const newTodo: CreateTodoRequest = {
          title: `${baseTodo.title} (${totalExisting + i + 1})`,
          description: baseTodo.description ?? undefined,
          status: 'todo',
          priority,
          due_date: formatISO(currentDate),
          organization_id: baseTodo.organization_id ?? undefined,
          parent_id: baseTodoId,
        };

        const createResult = await this.createSingleTodo(newTodo, baseTodo.created_by ?? undefined);
        if (createResult.success) {
          newInstances.push(createResult.data!);
        } else {
          logger.warn('Failed to create expanded instance', {
            instance: totalExisting + i + 1,
            error: createResult.error
          });
          break;
        }
      }

      logger.info('Recurring instances expanded', {
        baseTodoId,
        addedInstances: newInstances.length,
        totalInstances: totalExisting + newInstances.length
      });

      return createSuccessResponse(newInstances);

    } catch (error) {
      logger.error('Error in expandRecurringInstances', {
        error: error instanceof Error ? error.message : error,
        baseTodoId
      });
      return createErrorResponse(error instanceof Error ? error.message : 'Expand recurring instances failed');
    }
  }

  async updateRecurrencePattern(todoId: string, pattern: RecurrencePattern): Promise<ServiceResponse<Todo>> {
    try {
      logger.debug('Updating recurrence pattern', { todoId, pattern });

      // Update the recurrence_pattern_id if pattern exists, or clear is_recurring if not
      const updateData: Record<string, unknown> = {
        updated_at: new Date().toISOString(),
      };
      if (pattern && pattern.frequency) {
        updateData.is_recurring = true;
      } else {
        updateData.is_recurring = false;
      }

      const { data, error } = await typedSupabase
        .from('todos')
        .update(updateData as unknown as Partial<{ is_recurring: boolean; updated_at: string }>)
        .eq('id', todoId)
        .select()
        .single();

      if (error) {
        logger.error('Failed to update recurrence pattern', { error: error.message, todoId });
        throw error;
      }

      logger.info('Recurrence pattern updated', { todoId, pattern: pattern.frequency });
      return createSuccessResponse(data as Todo);

    } catch (error) {
      logger.error('Error in updateRecurrencePattern', {
        error: error instanceof Error ? error.message : error,
        todoId
      });
      return createErrorResponse(error instanceof Error ? error.message : 'Update recurrence pattern failed');
    }
  }

  async getNextOccurrence(baseTodoId: string): Promise<ServiceResponse<Todo | null>> {
    try {
      logger.debug('Getting next occurrence', { baseTodoId });

      // Get existing instances
      const { data: instances, error } = await typedSupabase
        .from('todos')
        .select('*')
        .eq('recurring_parent_id', baseTodoId)
        .order('due_date', { ascending: false })
        .limit(1);

      if (error) {
        logger.error('Failed to get next occurrence', { error: error.message, baseTodoId });
        throw error;
      }

      if (!instances || instances.length === 0) {
        return createSuccessResponse(null);
      }

      const latestInstance = instances[0];
      const baseTodo = await this.getBaseTodo(baseTodoId);
      const pattern = this.inferRecurrencePattern(baseTodo, instances as Todo[]);

      if (!pattern) {
        return createSuccessResponse(null);
      }

      const nextDate = this.calculateNextOccurrence(
        new Date(latestInstance.due_date ?? latestInstance.created_at),
        pattern
      );

      // Create a preview of the next todo (not persisted)
      const nextTodo = {
        ...latestInstance,
        id: `preview-${Date.now()}`,
        title: `${latestInstance.title.split(' (')[0]} (${instances.length + 2})`,
        due_date: formatISO(nextDate),
        status: 'todo',
        created_at: formatISO(new Date()),
        updated_at: formatISO(new Date()),
      };

      return createSuccessResponse(nextTodo as Todo);

    } catch (error) {
      logger.error('Error in getNextOccurrence', {
        error: error instanceof Error ? error.message : error,
        baseTodoId
      });
      return createErrorResponse(error instanceof Error ? error.message : 'Get next occurrence failed');
    }
  }

  async getRecurringInstances(baseTodoId: string): Promise<ServiceResponse<Todo[]>> {
    try {
      logger.debug('Getting recurring instances', { baseTodoId });

      const { data, error } = await typedSupabase
        .from('todos')
        .select('*')
        .eq('recurring_parent_id', baseTodoId)
        .order('due_date', { ascending: true });

      if (error) {
        logger.error('Failed to get recurring instances', { error: error.message, baseTodoId });
        throw error;
      }

      logger.debug('Recurring instances retrieved', { baseTodoId, count: data.length });
      return createSuccessResponse(data as Todo[]);

    } catch (error) {
      logger.error('Error in getRecurringInstances', {
        error: error instanceof Error ? error.message : error,
        baseTodoId
      });
      return createErrorResponse(error instanceof Error ? error.message : 'Get recurring instances failed');
    }
  }

  // Private helper methods

  private async createSingleTodo(
    todo: CreateTodoRequest,
    createdBy?: string
  ): Promise<ServiceResponse<Todo>> {
    const { data, error } = await typedSupabase
      .from('todos')
      .insert({
        ...todo,
        created_by: createdBy ?? todo.assigned_by ?? 'system',
        updated_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) {
      return createErrorResponse(error.message);
    }

    return createSuccessResponse(data as Todo);
  }

  private calculateNextOccurrence(currentDate: Date, pattern: RecurrencePattern): Date {
    switch (pattern.frequency) {
      case 'daily': {
        return addDays(currentDate, pattern.interval);
      }
      case 'weekly': {
        return addDays(currentDate, pattern.interval * 7);
      }
      case 'monthly': {
        const nextMonth = new Date(currentDate);
        nextMonth.setMonth(nextMonth.getMonth() + pattern.interval);
        return nextMonth;
      }
      case 'yearly': {
        const nextYear = new Date(currentDate);
        nextYear.setFullYear(nextYear.getFullYear() + pattern.interval);
        return nextYear;
      }
      default:
        return addDays(currentDate, 1); // Default to daily
    }
  }

  private inferRecurrencePattern(baseTodo: Todo, instances: Todo[]): RecurrencePattern | null {
    if (instances.length < 2) {
      return null;
    }

    // Simple pattern inference - calculate average interval
    const dates = [new Date(baseTodo.due_date ?? baseTodo.created_at), ...instances.map(i => new Date(i.due_date ?? i.created_at))];
    const intervals: number[] = [];

    for (let i = 1; i < dates.length; i++) {
      intervals.push(dates[i].getTime() - dates[i-1].getTime());
    }

    const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
    const avgIntervalDays = avgInterval / (1000 * 60 * 60 * 24);

    // Infer frequency based on average interval
    if (avgIntervalDays < 1.5) {
      return { frequency: 'daily', interval: 1 };
    }
    if (avgIntervalDays < 10) {
      return { frequency: 'weekly', interval: Math.round(avgIntervalDays / 7) };
    }
    if (avgIntervalDays < 40) {
      return { frequency: 'monthly', interval: Math.round(avgIntervalDays / 30) };
    }
    return { frequency: 'yearly', interval: Math.round(avgIntervalDays / 365) };
  }

  private async getBaseTodo(todoId: string): Promise<Todo> {
    const { data } = await typedSupabase
      .from('todos')
      .select('*')
      .eq('id', todoId)
      .single();

    return data as Todo;
  }
}

// Export singleton instance
export const todoRecurrenceService = new TodoRecurrenceService();
